import Vue from 'vue';
import DataTransfersAPI from '../../api/dataTransfers';
import { throwErrorMessage } from '../utils/api';

// Estilo de modulo "arquivo unico" com constantes locais de mutation (releaseNotes.js), e nao o
// registro global mutation-types.js — nada fora deste modulo precisa destas mutations.
const types = {
  SET_UI_FLAG: 'SET_DATA_TRANSFERS_UI_FLAG',
  SET_TRANSFERS: 'SET_DATA_TRANSFERS',
  SET_TRANSFER: 'SET_DATA_TRANSFER',
  SET_META: 'SET_DATA_TRANSFERS_META',
  SET_PREVIEW: 'SET_DATA_TRANSFER_PREVIEW',
};

// `cancelling` NAO e terminal: o rollback esta andando e o card mostra o placar dele (#1551).
const TERMINAL_STATUSES = ['completed', 'failed', 'cancelled'];

// Uma transferencia de importacao/exclusao tem dois arquivos (enviado e falhas), cada um com a
// sua previa.
export const previewKey = (id, file) => `${id}:${file || 'default'}`;

// O Rails manda os dois formatos no mesmo cabeçalho:
//   attachment; filename="arquivo.csv"; filename*=UTF-8''arquivo.csv
// Um /filename="?(.+?)"?$/ ancorado no fim engole o header inteiro e o arquivo chega ao disco
// chamado `arquivo.csv_; filename_=UTF-8''arquivo.csv`. A forma RFC 5987 tem precedência porque
// é a única que preserva acento.
export const parseFileNameFromDisposition = (disposition, fallback) => {
  if (!disposition) return fallback;

  const encoded = disposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (encoded) {
    try {
      return decodeURIComponent(encoded[1]);
    } catch (error) {
      // percent-encoding malformado: cai para as formas simples abaixo
    }
  }

  const quoted = disposition.match(/filename="([^"]+)"/i);
  if (quoted) return quoted[1];

  const bare = disposition.match(/filename=([^;]+)/i);
  if (bare) return bare[1].trim();

  return fallback;
};

export const state = {
  records: {},
  sortOrder: [],
  // Previas em tabela, por previewKey. Mapa PROPRIO, fora de `records`: o SET_TRANSFERS do
  // polling substitui `records` inteiro a cada 5s e fecharia a tabela aberta.
  previews: {},
  meta: { currentPage: 1, totalPages: 1, totalCount: 0, perPage: 25 },
  uiFlags: {
    isFetching: false,
    isDownloading: false,
  },
};

export const getters = {
  getTransfers($state) {
    // sortOrder preserva a ordem que o servidor devolveu (mais recente primeiro); o mapa
    // records existe para permitir atualizacao pontual sem remontar a lista inteira.
    return $state.sortOrder.map(id => $state.records[id]).filter(Boolean);
  },
  getTransfer: $state => id => $state.records[id],
  getPreview: $state => (id, file) => $state.previews[previewKey(id, file)],
  getMeta($state) {
    return $state.meta;
  },
  getUIFlags($state) {
    return $state.uiFlags;
  },
  // Enquanto existir transferencia viva a tela precisa continuar consultando; quando todas
  // terminam o polling para sozinho, em vez de bater no servidor para sempre.
  hasActiveTransfers($state) {
    return $state.sortOrder.some(id => {
      const record = $state.records[id];
      return record && !TERMINAL_STATUSES.includes(record.status);
    });
  },
};

export const actions = {
  get: async ({ commit }, params = {}) => {
    commit(types.SET_UI_FLAG, { isFetching: true });
    try {
      const response = await DataTransfersAPI.get(params);
      const { payload = [], meta = {} } = response.data || {};
      commit(types.SET_TRANSFERS, payload);
      commit(types.SET_META, meta);
      return payload;
    } catch (error) {
      return throwErrorMessage(error);
    } finally {
      commit(types.SET_UI_FLAG, { isFetching: false });
    }
  },

  // Recarrega em silencio (sem acender isFetching) para o polling nao piscar a lista a cada tick.
  refresh: async ({ commit }, params = {}) => {
    try {
      const response = await DataTransfersAPI.get(params);
      const { payload = [], meta = {} } = response.data || {};
      commit(types.SET_TRANSFERS, payload);
      commit(types.SET_META, meta);
      return payload;
    } catch (error) {
      return [];
    }
  },

  show: async ({ commit }, id) => {
    try {
      const response = await DataTransfersAPI.show(id);
      const record = response.data?.payload || response.data;
      commit(types.SET_TRANSFER, record);
      return record;
    } catch (error) {
      return null;
    }
  },

  // Mantem as linhas anteriores enquanto recarrega (a tabela fica sob um veu em vez de piscar) e
  // nao lanca: o erro vira estado da previa, mostrado dentro do card.
  preview: async ({ commit }, { id, file }) => {
    const key = previewKey(id, file);
    commit(types.SET_PREVIEW, { key, data: { isLoading: true } });
    try {
      const response = await DataTransfersAPI.preview(id, { file });
      const payload = response.data?.payload || {};
      const data = {
        headers: payload.headers || [],
        rows: payload.rows || [],
        truncated: Boolean(payload.truncated),
        unreadable: Boolean(payload.unreadable),
        isLoading: false,
        hasError: false,
      };
      commit(types.SET_PREVIEW, { key, data });
      return data;
    } catch (error) {
      commit(types.SET_PREVIEW, {
        key,
        data: { isLoading: false, hasError: true },
      });
      return null;
    }
  },

  // Lanca a mensagem do servidor (ja terminou, gravando uma linha agora...) para o painel mostrar.
  cancel: async ({ commit }, id) => {
    try {
      const response = await DataTransfersAPI.cancel(id);
      const record = response.data?.payload || response.data;
      commit(types.SET_TRANSFER, record);
      return record;
    } catch (error) {
      return throwErrorMessage(error);
    }
  },

  download: async ({ commit }, { id, fileName, file }) => {
    commit(types.SET_UI_FLAG, { isDownloading: true });
    try {
      const response = await DataTransfersAPI.download(id, { file });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = parseFileNameFromDisposition(
        response.headers['content-disposition'],
        fileName || 'export.csv'
      );
      link.click();
      window.URL.revokeObjectURL(url);
      return true;
    } catch (error) {
      return throwErrorMessage(error);
    } finally {
      commit(types.SET_UI_FLAG, { isDownloading: false });
    }
  },
};

export const mutations = {
  [types.SET_UI_FLAG]($state, data) {
    $state.uiFlags = { ...$state.uiFlags, ...data };
  },
  [types.SET_TRANSFERS]($state, records) {
    const nextRecords = {};
    const nextOrder = [];
    records.forEach(record => {
      // Chaveado por `uid`, nao por `id`: a lista mistura data_imports com data_deletes, que tem
      // sequencias de id proprias, entao com o id puro uma exclusao de id 5 sobrescreveria a
      // importacao de id 5 e a lista perderia uma linha em silencio.
      const key = record.uid || record.id;
      nextRecords[key] = record;
      nextOrder.push(key);
    });
    // Substituicao em bloco: a pagina inteira foi recarregada, entao manter registros antigos
    // sujaria a lista com transferencias que sairam do filtro/pagina atual.
    $state.records = nextRecords;
    $state.sortOrder = nextOrder;
  },
  [types.SET_TRANSFER]($state, record) {
    if (!record?.id) return;

    const key = record.uid || record.id;
    // Vue 2: atribuicao direta numa chave nova do mapa nao e reativa.
    Vue.set($state.records, key, {
      ...($state.records[key] || {}),
      ...record,
    });
    if (!$state.sortOrder.includes(key)) {
      $state.sortOrder.unshift(key);
    }
  },
  [types.SET_PREVIEW]($state, { key, data }) {
    // Vue 2: chave nova no mapa so e reativa via Vue.set.
    Vue.set($state.previews, key, {
      headers: [],
      rows: [],
      truncated: false,
      unreadable: false,
      ...($state.previews[key] || {}),
      ...data,
    });
  },
  [types.SET_META]($state, meta) {
    $state.meta = {
      currentPage: meta.current_page ?? $state.meta.currentPage,
      totalPages: meta.total_pages ?? $state.meta.totalPages,
      totalCount: meta.total_count ?? $state.meta.totalCount,
      perPage: meta.per_page ?? $state.meta.perPage,
    };
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
